import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getEmailTemplates, createEmailTemplate, updateEmailTemplate, deleteEmailTemplate, importEmailTemplate, duplicateEmailTemplate } from "api/bMarketingApi";

export function useMarketingEmailTemplates(organisationId) {
  const queryClient = useQueryClient();

  const { data: emailTemplates, isLoading, error } = useQuery({
    queryKey: ["emailTemplates", organisationId],
    queryFn: () => getEmailTemplates(organisationId).then((res) => res.data),
    enabled: !!organisationId,
  });

  const createMutation = useMutation({
    mutationFn: createEmailTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries(["emailTemplates", organisationId]);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ templateId, data }) => updateEmailTemplate(templateId, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["emailTemplates", organisationId]);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteEmailTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries(["emailTemplates", organisationId]);
      // Also invalidate automations since they may have been deleted with the template
      queryClient.invalidateQueries(["automations"]);
    },
  });

  const importMutation = useMutation({
    mutationFn: importEmailTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries(["emailTemplates", organisationId]);
    },
  });

  const duplicateMutation = useMutation({
    mutationFn: ({ templateId, data }) => duplicateEmailTemplate(templateId, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["emailTemplates", organisationId]);
    },
  });

  return {
    emailTemplates,
    isLoading,
    error,
    createTemplate: createMutation.mutate,
    createTemplateAsync: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    updateTemplate: updateMutation.mutate,
    isUpdating: updateMutation.isPending,
    deleteTemplate: deleteMutation.mutate,
    isDeleting: deleteMutation.isPending,
    importTemplate: importMutation.mutate,
    importTemplateAsync: importMutation.mutateAsync,
    isImporting: importMutation.isPending,
    duplicateTemplate: duplicateMutation.mutate,
    duplicateTemplateAsync: duplicateMutation.mutateAsync,
    isDuplicating: duplicateMutation.isPending,
  };
}
